import React, {
    useEffect,
    useRef,
    useContext,
    Fragment,
    useState,
} from "react";
import { Formik, Form } from "formik";
import DominantMediaField from "Screens/Article/Fields/DominantMediaField";
import TextInput from "components/FormElements/TextInput";
import FastTextInput from "components/FormElements/FastTextInput";
import Label from "components/FormElements/Label";
import ArticleEditor from "components/FormElements/ArticleEditor";
import WorkflowField from "./Fields/WorkflowField";
import AuthorField from "./Fields/AuthorField";
import TagField from "./Fields/TagField";
import UuidField from "./Fields/UuidField";
import ArticleMeta from "./Fields/ArticleMeta";
import ArticleToolbar from "./ArticleToolbar";
import TabContainer, { TabPanel } from "components/Surfaces/TabContainer";
import VersionStatusField from "./Fields/VersionStatusField";
import StaticDateField from "./Fields/StaticDateField";
import { ContentContext } from "context/Content";
import PublishButton from "./Fields/PublishButton";
import RichEditor from "components/FormElements/RichEditor";
import TextArea from "components/FormElements/TextArea";
import SstsField from "./Fields/SstsField";
import ExportButton from "./Fields/ExportButton";
import SocialPreviews from "./SocialPreviews";
import HistoryButton from "./Fields/HistoryButton";
import LayoutTemplateField from "./Fields/LayoutTemplateField";
import UrlField from "./Fields/UrlField";
import { useContextualHelp } from "hooks/Help";
import Dam from "./Dam";
import CopyDesk from "./CopyDesk";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import KBarActions from "./KbarActions";
import { useAuth } from "hooks/Auth";
import { LockClosedIcon, ExclamationIcon } from "@heroicons/react/outline";
import { fromSqlDateTime } from "helpers/Date";
import { useSelector } from "react-redux";
import * as KVStore from "helpers/KVStore";
import { useHistory } from "react-router-dom";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import HotTip from "components/Notifications/HotTip";
import { set } from "lodash";
import { packClasses } from "Utils";

function EditForm(props) {
    const formRef = useRef();
    const contextualHelp = useContextualHelp();
    const { state, user } = useContext(ContentContext);
    const auth = useAuth();
    const settings = useSelector((state) => state.contentFlow?.settings);
    const [recovered, setRecovered] = useState(false);
    const [hedCount, setHedCount] = useState(0);
    const history = useHistory();

    const handleRecovery = () => {
        history.push(`/app/article/${state.content.uuid}?recovery=true`);
    };
    const removeRecovery = () => {
        KVStore.remove(state.content.uuid);
        setRecovered(false);
    };

    const flatNormalize = (s) => {
        return s.replace(/[^a-zA-Z0-9]/gm, "");
    };

    const autoSaveCallback = (d) => {
        if (settings.settings?.localSave !== true) {
            return;
        }
        if (!state.content.uuid) {
            return;
        }
        if (flatNormalize(d) === flatNormalize(state.content.content)) {
            return;
        }
        KVStore.set(state.content.uuid, d);
    };

    useEffect(() => {
        if (state.content.uuid) {
            KVStore.get(state.content.uuid).then((resp) => {
                setRecovered(resp);
            });
            setHedCount(state.content?.title?.length);
        }
    }, [state.content.uuid]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    return (
        <Fragment>
            <Formik
                innerRef={formRef}
                initialValues={state.formValues}
                onSubmit={props.onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <KBarActions />
                        <PromptIfDirty />
                        <div className="relative">
                            <div className="sticky top-0 z-10">
                                <ArticleToolbar />
                            </div>
                            <div className="grid grid-cols-12 gap-4 px-2 sm:px-0">
                                <div className="order-last col-span-12 space-y-4 md:col-span-3">
                                    <UuidField />
                                    <ExportButton />
                                    <HistoryButton />

                                    <div className="space-y-4 rounded bg-white p-4 shadow-sm">
                                        <VersionStatusField />
                                        <StaticDateField
                                            label="Created At:"
                                            field="created_at"
                                            defaultValue="Unsaved"
                                        />
                                        <StaticDateField
                                            label="Modified At:"
                                            field="modified_at"
                                            defaultValue="Unsaved"
                                        />
                                        <StaticDateField
                                            label="Published At:"
                                            field="published_at"
                                            defaultValue="Draft"
                                        />
                                        <hr />
                                        <LayoutTemplateField />
                                        <hr />
                                        <div className="mx-0 flex items-end justify-evenly space-x-4 px-0">
                                            <TextInput
                                                name="weight"
                                                label="Weight"
                                                type="number"
                                                min={0}
                                                max={5}
                                            />
                                            <div className="flex-grow">
                                                <PublishButton />
                                            </div>
                                        </div>
                                        <div className="mt-1 text-xs text-gray-500">
                                            <button
                                                type="button"
                                                className="cursor-help text-green-600 underline"
                                                onClick={() => {
                                                    contextualHelp.showContextualHelp(
                                                        "/article/weight.html"
                                                    );
                                                }}
                                            >
                                                Weight
                                            </button>{" "}
                                            impacts automatic sorting of
                                            content.
                                        </div>
                                    </div>

                                    <div className="rounded bg-white p-4 shadow-sm">
                                        <WorkflowField />
                                    </div>

                                    <div className="rounded bg-white p-4 shadow-sm">
                                        <TagField />
                                    </div>

                                    <div className="rounded bg-white p-4 shadow-sm">
                                        <AuthorField />
                                    </div>
                                </div>
                                <div className="order-first col-span-12 space-y-4 md:col-span-9">
                                    {!state.loading &&
                                        state.content.lock?.uuid &&
                                        state.content.lock?.user?.uuid !==
                                            auth.user.uuid && (
                                            <div className="mb-4 rounded bg-white p-4 text-sm font-normal text-gray-800 shadow-sm">
                                                <LockClosedIcon className="inline h-5 w-5" />{" "}
                                                Checked out by{" "}
                                                {state.content.lock.user.name}{" "}
                                                on{" "}
                                                {fromSqlDateTime(
                                                    state.content.lock
                                                        .created_at
                                                ).toFormat("ff")}
                                            </div>
                                        )}
                                    <DominantMediaField
                                        label="dominantAttachment"
                                        name="dominantAttachment"
                                        disabled={!state.isUserEditable}
                                    />

                                    {state.loading ? (
                                        <Fragment>
                                            <div>
                                                <Label>Title</Label>
                                                <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                            </div>
                                            <div>
                                                <Label>Slug</Label>
                                                <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                            </div>
                                            <div>
                                                <Label>URL Slug</Label>
                                                <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                            </div>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <div>
                                                <div className="flex items-end space-x-4">
                                                    <div class="flex-grow">
                                                        <TextInput
                                                            label="Title"
                                                            name="title"
                                                            disabled={
                                                                state.loading ||
                                                                state.preformingStateChange ||
                                                                !state.isUserEditable
                                                            }
                                                            onChange={(e) => {
                                                                setHedCount(
                                                                    e.target
                                                                        .value
                                                                        .length
                                                                );
                                                                setFieldValue(
                                                                    "title",
                                                                    e.target
                                                                        .value
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                    <div
                                                        className={packClasses(
                                                            "flex flex-col text-center text-sm ",
                                                            hedCount > 85
                                                                ? "text-red-800"
                                                                : ""
                                                        )}
                                                    >
                                                        {hedCount}
                                                        <span className="text-xs font-extralight uppercase">
                                                            Chars
                                                        </span>
                                                    </div>
                                                </div>
                                                <HotTip>
                                                    Read more:{" "}
                                                    <a
                                                        href="https://www.poynter.org/educators-students/2017/9-tips-for-writing-stronger-headlines/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-green-600 underline"
                                                    >
                                                        Poynter: Tips for
                                                        writing stronger
                                                        headlines
                                                    </a>
                                                    .
                                                </HotTip>
                                            </div>

                                            <TextInput
                                                label="Slug"
                                                name="slug"
                                                placeholder="Leave blank to auto-generate"
                                                disabled={
                                                    state.loading ||
                                                    state.preformingStateChange ||
                                                    !state.isUserEditable
                                                }
                                                onChange={(e) => {
                                                    const newVal =
                                                        e.target.value
                                                            .toLowerCase()
                                                            .replace(
                                                                / {2,}/g,
                                                                " "
                                                            )
                                                            .replace(/ /g, "-")
                                                            .replace(
                                                                /[^a-z0-9-]/g,
                                                                ""
                                                            );

                                                    setFieldValue(
                                                        "slug",
                                                        newVal
                                                    );
                                                }}
                                                helpertext={
                                                    <Fragment>
                                                        Slugs are internal
                                                        references and depend on
                                                        your publication's
                                                        general workflow.
                                                        Changing a slug after
                                                        publication will
                                                        <button
                                                            type="button"
                                                            className="cursor-help text-green-600 underline"
                                                            onClick={() => {
                                                                contextualHelp.showContextualHelp(
                                                                    "/article/index.html#changing-urls"
                                                                );
                                                            }}
                                                        >
                                                            generate an
                                                            additional URL.
                                                        </button>
                                                    </Fragment>
                                                }
                                            />
                                            <TextInput
                                                label="URL Slug"
                                                name="title_url"
                                                placeholder="Leave blank to auto-generate"
                                                disabled={
                                                    state.loading ||
                                                    state.preformingStateChange ||
                                                    !state.isUserEditable
                                                }
                                                onChange={(e) => {
                                                    const newVal =
                                                        e.target.value
                                                            .toLowerCase()
                                                            .replace(
                                                                / {2,}/g,
                                                                " "
                                                            )
                                                            .replace(/ /g, "-")
                                                            .replace(
                                                                /[^a-z0-9-]/g,
                                                                ""
                                                            );

                                                    setFieldValue(
                                                        "title_url",
                                                        newVal
                                                    );
                                                }}
                                                helpertext="URL slugs are used to generate the URL for the article, they should be short but descriptive."
                                            />
                                            <div>
                                                <SstsField />
                                                <div className="mt-1 text-xs text-gray-500">
                                                    SSTS values are used to{" "}
                                                    <button
                                                        onClick={() => {
                                                            contextualHelp.showContextualHelp(
                                                                "/article/index.html#ssts-vs-tags"
                                                            );
                                                        }}
                                                        type="button"
                                                        className="cursor-help text-green-600 underline"
                                                    >
                                                        classify content
                                                    </button>{" "}
                                                    , but may not be supported
                                                    by your template.
                                                </div>
                                            </div>
                                        </Fragment>
                                    )}
                                    <UrlField />
                                </div>
                            </div>
                        </div>

                        <div className="relative z-20 mt-4">
                            {state.loading ? (
                                <div className="absolute top-0 left-0 z-50 h-full w-full bg-gray-50">
                                    <div className="h-full w-full animate-pulse rounded bg-gray-300"></div>
                                </div>
                            ) : null}
                            <TabContainer>
                                <TabPanel label="Content">
                                    <div className="space-y-4">
                                        <div>
                                            <Label>Content</Label>
                                            {recovered && (
                                                <div className="mb-4 rounded bg-white p-4 text-sm font-normal text-gray-800 shadow-sm">
                                                    <ExclamationIcon className="inline h-5 w-5" />{" "}
                                                    There may be unsaved changes
                                                    available for recovery.
                                                    <PrimaryButton
                                                        label="Recover"
                                                        variant="xs"
                                                        className="mx-2"
                                                        onClick={handleRecovery}
                                                    />
                                                    <SecondaryButton
                                                        label="Discard"
                                                        variant="xs"
                                                        onClick={removeRecovery}
                                                    />
                                                </div>
                                            )}
                                            <ArticleEditor
                                                name="content"
                                                clips={
                                                    settings.settings?.clips ||
                                                    []
                                                }
                                                cleanPaste={
                                                    settings.settings
                                                        ?.ffCleanPaste || false
                                                }
                                                externalCss={
                                                    settings.settings
                                                        ?.editorCss || ""
                                                }
                                                onRequestNavigate={
                                                    props.onRequestNavigate
                                                }
                                                onCreateNewCopyRef={
                                                    props.onCreateNewCopyRef
                                                }
                                                onRequestLoadCopyRef={
                                                    props.onRequestLoadCopyRef
                                                }
                                                onRequestSave={() => {
                                                    formRef.current.submitForm();
                                                }}
                                                disabled={
                                                    state.loading ||
                                                    state.preformingStateChange ||
                                                    !state.isUserEditable
                                                }
                                                autoSaveCallback={
                                                    autoSaveCallback
                                                }
                                                editorName={user.name}
                                            />
                                            <HotTip>
                                                Trying to select all content?
                                                Use CTRL/CMD+A twice! What about
                                                smart quotes? Check out{" "}
                                                <a
                                                    href="https://smartquotesforsmartpeople.com/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-green-600 underline"
                                                >
                                                    Smart Quotes for Smart
                                                    People
                                                </a>
                                                .
                                            </HotTip>
                                        </div>
                                        <div>
                                            <Label>Abstract</Label>
                                            <RichEditor name="abstract" />
                                        </div>
                                    </div>
                                </TabPanel>
                                <TabPanel label="Meta">
                                    <ArticleMeta block="content" />
                                    <ArticleMeta block="content-lower" />
                                </TabPanel>
                                <TabPanel label="SEO">
                                    <div className="mx-auto mb-4 max-w-screen-md space-y-4 rounded bg-white p-4">
                                        <p className="prose-sm text-gray-800">
                                            CEO will generate sensible SEO data
                                            for you automatically, but you can
                                            override it here.
                                        </p>
                                    </div>
                                    <div className="space-y-4">
                                        <SocialPreviews />
                                        <div>
                                            <Label>SEO Headline/Title</Label>
                                            <TextInput name="seo_title" />
                                        </div>
                                        <div>
                                            <Label>
                                                SEO Abstract/Description
                                            </Label>
                                            <TextArea
                                                name="seo_description"
                                                rows={3}
                                            />
                                        </div>
                                        <div>
                                            <Label>SEO Image</Label>
                                            <TextInput name="seo_url" />
                                        </div>
                                    </div>
                                </TabPanel>
                                <TabPanel label="DAM">
                                    <div className="mx-auto mb-4 max-w-screen-md space-y-4 rounded bg-white p-4">
                                        <p className="prose-sm text-gray-800">
                                            Digital Asset Management (DAM) is an
                                            important feature that helps you
                                            track, collect, and curate your
                                            content as it grows.
                                        </p>
                                    </div>
                                    <Dam item={state.content} />
                                </TabPanel>
                                <TabPanel label="CopyDesk">
                                    <div className="mx-auto mb-4 max-w-screen-md space-y-4 rounded bg-white p-4">
                                        <p className="prose-sm text-gray-800">
                                            CopyDesk is a feature of CEO that
                                            allows you to collect and maintain
                                            supporting documents, notes, and
                                            conversations for your content.
                                        </p>
                                    </div>
                                    <CopyDesk item={state.content} />
                                </TabPanel>
                            </TabContainer>
                        </div>
                    </Form>
                )}
            </Formik>
        </Fragment>
    );
}

export default EditForm;
